import { useState, useEffect } from "react";
import { useQuery, useMutation, ApolloError } from "@apollo/client";
import {
    getAllProductionLines,
    getProductionLinesAndHMIs,
    getProductionLinesCorporateBO,
    insertProductionLineCorporateBO,
} from "../../api/queries/productionLinesAPI";
import {
    IProductionLine,
    IProductionLinesEntity,
    IProductionLineWithOrderedMachineCenters,
} from "./ProductionLinesInterfaces";

export const ProductionLinesEntity: IProductionLinesEntity = {
    GetProductionLinesCorporateBO: (millId) => {
        const { loading, error, data, refetch } = useQuery(getProductionLinesCorporateBO, {
            fetchPolicy: "no-cache",
            variables: { millId: millId },
        });
        const [productionLines, setProductionLines] = useState<IProductionLine[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const productionLinesArray: IProductionLine[] = data.production_line;
                setProductionLines(productionLinesArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, productionLines, apolloQueryError, refetch };
    },
    InsertProductionLineCorporateBO: () => {
        const [addProductionLine, { error }] = useMutation(insertProductionLineCorporateBO);

        return { apolloMutationError: error, addProductionLine };
    },
    GetAllProductionLines: () => {
        const { data, error, loading, refetch } = useQuery(getAllProductionLines);
        const [productionLines, setProductionLines] = useState<IProductionLine[]>([]);

        useEffect(() => {
            if (data) {
                setProductionLines(data.production_line);
            }
        }, [data]);

        return { error, productionLines, loading, refetch };
    },
    GetProductionLinesAndHMIs: (mill_id: number) => {
        const { loading, error, data } = useQuery(getProductionLinesAndHMIs, {
            fetchPolicy: "no-cache",
            variables: { mill_id },
        });
        let productionLines: IProductionLine[] = [] as IProductionLine[];
        let corporateId = 0;
        if (data) {
            productionLines = data.production_line;
            corporateId = data?.corporate[0]?.corporate_id;
        }
        return { loading, productionLines, corporateId, error };
    },
    GetProductionLinesCorporateWithOrderedMCBO: (millId) => {
        const [productionLines, setProductionLines] = useState<IProductionLineWithOrderedMachineCenters[]>([]);

        const sortMachineCenters = (machineCenters: IProductionLine["machine_centers"]) => {
            if (machineCenters.some((machineCenter) => machineCenter.order === null)) {
                return machineCenters;
            } else {
                return [...machineCenters].sort((a, b) => Number(a.order) - Number(b.order));
            }
        };

        const { refetch } = useQuery(getProductionLinesCorporateBO, {
            fetchPolicy: "no-cache",
            onCompleted: (data) => {
                if (data.production_line) {
                    const productionLinesArray: IProductionLine[] = data.production_line;
                    const productionLinesOptions = productionLinesArray.map((plOption) => ({
                        id: plOption.production_line_id,
                        name: plOption.name,
                        highestOrderNumber: Math.max(...plOption.machine_centers.map((mc) => mc.order ?? 0)),
                    }));
                    const productionLinesWithSortedMachineCenters: IProductionLineWithOrderedMachineCenters[] =
                        productionLinesArray.map((pl) => {
                            return {
                                ...pl,
                                machine_centers: sortMachineCenters(pl.machine_centers),
                                highestOrderNumber: Math.max(...pl.machine_centers.map((mc) => mc.order ?? 0)),
                                productionLineOptions: productionLinesOptions,
                            };
                        });
                    setProductionLines(productionLinesWithSortedMachineCenters);
                }
            },
            variables: { millId: millId },
        });

        return {
            productionLines,
            refetch,
            setProductionLines,
        };
    },
};
